import React, { useEffect, useState } from 'react';
import { Switch } from '../ui/Field';
import { pillPrimary } from '../ui/pills';
import { messageErreur, reservationsSaisieApi, type ChampFormulaire, type FormulaireResa } from '../../lib/reservationsApi';
import { useCodeManager } from './useCodeManager';

/**
 * « Formulaire de réservation » (comme ZenChef) : pour chaque champ de la saisie d'une réservation par l'équipe, s'il est affiché et
 * s'il est obligatoire. Le nom est toujours demandé. Le serveur applique les mêmes règles à l'enregistrement.
 */
const ORDRE: ChampFormulaire[] = ['civilite', 'prenom', 'nom', 'telephone', 'email', 'occasion', 'commentaire_client', 'notes_internes'];

export const FormulaireReglages: React.FC = () => {
    const { executer, fenetre } = useCodeManager();
    const [champs, setChamps] = useState<FormulaireResa['champs'] | null>(null);
    const [libelles, setLibelles] = useState<Record<ChampFormulaire, { label: string; fixe: boolean }> | null>(null);
    const [message, setMessage] = useState('');
    const [erreur, setErreur] = useState('');
    const [occupe, setOccupe] = useState(false);

    useEffect(() => {
        reservationsSaisieApi.formulaire().then((r) => { setChamps(r.champs); setLibelles(r.libelles); }).catch((e) => setErreur(messageErreur(e)));
    }, []);

    const maj = (k: ChampFormulaire, v: Partial<{ afficher: boolean; obligatoire: boolean }>) => {
        setMessage('');
        setChamps((c) => {
            if (!c) return c;
            const n = { ...c[k], ...v };
            if (!n.afficher) n.obligatoire = false;
            return { ...c, [k]: n };
        });
    };
    const enregistrer = async () => {
        if (!champs) return;
        setOccupe(true); setErreur(''); setMessage('');
        try {
            const r = await executer((code) => reservationsSaisieApi.enregistrerFormulaire(champs, code));
            if (r) { setChamps(r.champs); setMessage('Formulaire enregistré.'); }
        } catch (e) { setErreur(messageErreur(e)); } finally { setOccupe(false); }
    };

    return (
        <section className="bg-surface border border-border rounded-xl p-4 space-y-3" aria-labelledby="titre-formulaire">
            <div>
                <h2 id="titre-formulaire" className="text-[15px] font-semibold text-content">Formulaire de réservation</h2>
                <p className="text-[12.5px] text-content-muted mt-0.5">Les champs demandés quand l'équipe note une réservation, et ceux qui sont obligatoires.</p>
            </div>
            {!champs || !libelles ? (erreur ? <p role="alert" className="text-[13px] text-state-danger">{erreur}</p> : <p className="text-[13px] text-content-muted">Chargement…</p>) : (
                <>
                    <ul className="divide-y divide-border-soft">
                        {ORDRE.map((k) => (
                            <li key={k} className="flex flex-wrap items-center justify-between gap-3 py-2.5">
                                <span className="text-[13px] font-semibold text-content min-w-[160px]">{libelles[k]?.label || k}</span>
                                {libelles[k]?.fixe ? <span className="text-[12.5px] text-content-muted">Toujours demandé et obligatoire</span> : (
                                    <div className="flex flex-wrap items-center gap-5">
                                        <Switch checked={champs[k].afficher} onChange={(e) => maj(k, { afficher: e.target.checked })} label="Afficher" />
                                        <Switch checked={champs[k].obligatoire} disabled={!champs[k].afficher} onChange={(e) => maj(k, { obligatoire: e.target.checked })} label="Obligatoire" />
                                    </div>
                                )}
                            </li>
                        ))}
                    </ul>
                    {erreur && <p role="alert" className="rounded-xl bg-state-danger-soft text-state-danger px-3.5 py-2.5 text-[13px] font-semibold">{erreur}</p>}
                    {message && <p role="status" className="text-[13px] font-semibold text-state-ok">{message}</p>}
                    <div className="flex justify-end"><button type="button" className={`${pillPrimary(36)} max-lg:!h-11`} disabled={occupe} onClick={() => void enregistrer()}>Enregistrer le formulaire</button></div>
                </>
            )}
            {fenetre}
        </section>
    );
};
